'use client'

import React, { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Check, Loader, Minus } from 'lucide-react'
import {
  N_FREE_IMAGES,
  N_FREE_ITEMS,
  N_FREE_SECTIONS,
  N_FREE_DASHBOARD_BOOKINGS_MONTHLY,
  N_PREMIUM_IMAGES,
  N_PREMIUM_ITEMS,
  N_PREMIUM_SECTIONS,
  getAnnualPerMonth,
  getMonthlyPrice
} from '@/config'
import '@/sass/PricingComponent.scss'
import useSpecialButtons from '@/components/specialButtons/useSpecialButtons'
import EmbeddedCheckoutForm from '@/components/specialButtons/EmbeddedCheckoutForm'
import { useBarDataContext } from '@/contexts/BarDataContext'
import { Link } from '@/i18n/routing'
import { serverLog } from '@/lib/actions/serverLog'

interface Props {
	isForHome?: boolean
}

const PricingComponent = ({ isForHome = false }: Props) => {
  const { barData } = useBarDataContext()

  const t = useTranslations('PricingTable')
  const { buySubscription, clientSecret, checkoutUi, resetCheckout, checkoutError } = useSpecialButtons()
  const [period, setPeriod] = useState<'monthly' | 'annual'>('annual')
  const [loading, setLoading] = useState(false)
  // La moneda que cobra el servidor es `barData.currency`. El timezone es solo
  // un fallback para la página pública `/pricing` (no hay `barData` ahí) y así
  // lo mostrado coincide con lo cobrado.
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  const isAmerica = userTimeZone.toLowerCase().includes('america')
  const currencySymbol = barData?.currency || (isAmerica ? '$' : '€')
  const monthlyPrice = getMonthlyPrice(currencySymbol)
  const annualPerMonth = getAnnualPerMonth(currencySymbol)
  const freePrice = 0

  useEffect(() => {
    let isMounted = true

    const logPriceView = async () => {
      if (barData?.name && isMounted) {
        try {
          await serverLog(`Bar consultando precios: ${barData.name}`)
        } catch {
          // Silenciar errores si el componente se desmonta durante la llamada
        }
      }
    }

    logPriceView()

    return () => {
      isMounted = false
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formatPrice = (amount: number) => (currencySymbol === '€' ? `${amount}${currencySymbol}` : `${currencySymbol}${amount}`)

  const togglePeriod = (p: 'monthly' | 'annual') => setPeriod(p)

  const featuresList = [
    { key: t('numberOfProducts'), free: N_FREE_ITEMS, premium: N_PREMIUM_ITEMS },
    { key: t('numberOfSections'), free: N_FREE_SECTIONS, premium: N_PREMIUM_SECTIONS },
    { key: t('numberOfProductsWithImages'), free: N_FREE_IMAGES, premium: N_PREMIUM_IMAGES },
    { key: t('languages'), free: t('oneLanguage'), premium: t('multipleLanguages') },
    { key: t('maxVisitsPerMonth'), free: t('unlimited'), premium: t('unlimited') },
    { key: t('instantChanges'), free: true, premium: true },
    { key: t('customizableMenu'), free: true, premium: true },
    { key: t('analytics'), free: true, premium: true },
    { key: t('manualDashboardBookings'), free: t('bookingsPerMonth', { count: N_FREE_DASHBOARD_BOOKINGS_MONTHLY }), premium: t('unlimited'), tag: t('bookings') },
    { key: t('googleReviewsManagement'), free: true, premium: true },
    { key: t('deliveryPanel'), free: true, premium: true, tag: t('deliveryFeature') },
    { key: t('deliveryIntegrated'), free: true, premium: true, tag: t('deliveryFeature') },
    { key: t('deliveryTracking'), free: true, premium: true, tag: t('deliveryFeature') },
    { key: t('deliveryClientHistory'), free: true, premium: true, tag: t('deliveryFeature') },
    { key: t('adFreeMenu'), free: false, premium: true },
    { key: t('downloadPdfMenu'), free: false, premium: true },
    { key: t('promoteProductsWithPopup'), free: false, premium: true },
    { key: t('reservationEngineWithoutFees'), free: false, premium: true, tag: t('bookings') },
    { key: t('customerDatabase'), free: false, premium: true, tag: t('bookings') },
    { key: t('ownReservationPage'), free: false, premium: true, tag: t('bookings') },
    { key: t('reservationConfirmationService'), free: false, premium: true, tag: t('bookings') },
    { key: t('aiTranslations'), free: false, premium: true },
    { key: t('seoAndPositioning'), free: false, premium: true },
    { key: t('phoneSupport'), free: false, premium: true }
  ]

  const plans = [
    { name: t('free'), price: freePrice, suffix: '', isPremium: false },
    {
      name: t('premium'),
      price: period === 'monthly' ? monthlyPrice : annualPerMonth,
      suffix: t('perMonth'),
      isPremium: true
    }
  ]

  if (loading && !clientSecret) {
    return (
      <div className='widget widget-3-col upgrade-widget'>
        <div className='upgrade-checkout-loading' aria-label={t('preparingCheckout')} aria-live='polite'>
          <div className='upgrade-checkout-loading__spinner' />
        </div>
      </div>
    )
  }

  if (clientSecret) {
    return (
      <div className='widget widget-3-col upgrade-widget'>
        <EmbeddedCheckoutForm
          clientSecret={clientSecret}
          uiMode={checkoutUi}
          onClose={() => {
            resetCheckout()
            setLoading(false)
          }}
        />
      </div>
    )
  }

  return (
    <div className='widget widget-3-col upgrade-widget'>
      {checkoutError && <p role='alert'>{checkoutError}</p>}
      <div className='pricing-cards'>
        <div className='pricing-cards__toggle'>
          <button className={period === 'annual' ? 'active' : ''} onClick={() => togglePeriod('annual')}>
            {t('annual')}
          </button>
          <button className={period === 'monthly' ? 'active' : ''} onClick={() => togglePeriod('monthly')}>
            {t('monthly')}
          </button>
        </div>

        <p className='save-with-yearly'>{t('saveWithYearly')}</p>

        <div className='pricing-cards__cards'>
          {plans.map((plan) => {
            const visibleFeatures = featuresList.filter((feat) => Boolean(plan.isPremium ? feat.premium : feat.free))
            return (
              <div key={plan.name} className={`card ${plan.isPremium ? 'card--premium' : ''}`}>
                <div className='card__header'>
                  <h3>{plan.name}</h3>
                </div>
                <div className='card__price'>
                  <span>
                    {formatPrice(plan.price)}
                    {plan.suffix && <small className='price__suffix'>{plan.suffix}</small>}
                  </span>
                </div>
                <div className='card__desc'>
                  <p>{plan.isPremium ? t('premiumPlanDescription') : t('freePlanDescription')}</p>
                </div>
                <ul className='card__features'>
                  {visibleFeatures.map((feat, idx) => {
                    const included = plan.isPremium ? feat.premium : feat.free
                    const isBoolean = typeof included === 'boolean'
                    return (
                      <li className='card__feature' key={idx}>
                        <span>{feat.key}</span>
                        {feat.tag && <em className='feature__tag'>{feat.tag}</em>}
                        <strong>
                          {isBoolean
                            ? (included ? <Check size={16} className='check-icon' /> : <Minus size={16} className='minus-icon' />)
                            : included}
                        </strong>
                      </li>
                    )
                  })}
                </ul>
                {isForHome || plan.isPremium ? (
                  <div className='card__action'>
                    {isForHome
                      ? (
                          <Link
                            href='/login/signup'
                            className={`btn ${plan.isPremium ? 'btn--primary' : 'btn--outline'}`}
                          >
                            {plan.isPremium ? t('upgradeNow') : t('startFree')}
                          </Link>
                        )
                      : (
                          <>
                            <button
                              onClick={async () => {
                                setLoading(true)
                                await buySubscription(period === 'annual' ? 'yearly' : 'monthly')
                                setLoading(false)
                              }}
                              className='btn btn--primary'
                              disabled={loading}
                            >
                              {loading
                                ? (
                                  <>
                                    <Loader className='button-spinner' size={18} />
                                  </>
                                  )
                                : (
                                    t('upgradeNow')
                                  )}
                            </button>
                            <p className='sub-type'>{period === 'annual' ? t('anualSub') : t('monthlySub')}</p>
                          </>
                        )}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default PricingComponent
